import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Alert, Button, InputNumber, Select, Table, Tag, message } from 'antd';
import moment from 'moment';
import { GET_JOB_QUEUE_LIST, GET_JOB_QUEUE_STATS } from '../queries';
import { PROCESS_JOB_QUEUE, RETRY_JOB_QUEUE } from '../mutations';

const { Option } = Select;

const STATUS_COLORS = {
  QUEUED: 'blue',
  RUNNING: 'gold',
  COMPLETED: 'green',
  FAILED: 'red',
};

const JOB_TYPES = ['BUDGET_RECALC'];
const JOB_STATUSES = ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED'];

const formatDate = value => {
  if (!value) return '-';
  return moment(value).format('YYYY-MM-DD HH:mm:ss');
};

const formatAge = ms => {
  if (ms == null || Number.isNaN(Number(ms))) return '-';
  const duration = moment.duration(Number(ms), 'milliseconds');
  if (duration.asMinutes() < 1) return `${Math.max(1, Math.floor(duration.asSeconds()))}s`;
  if (duration.asHours() < 1) return `${Math.floor(duration.asMinutes())}m`;
  if (duration.asDays() < 1) return `${Math.floor(duration.asHours())}h`;
  return `${Math.floor(duration.asDays())}d`;
};

const buildListVariables = ({ jobType, statuses, page, limit, active }) => {
  const variables = { page, limit };
  if (jobType) variables.jobType = jobType;
  if (statuses?.length > 0) variables.statuses = statuses;
  if (active === 'true') variables.active = true;
  if (active === 'false') variables.active = false;
  return variables;
};

const JobQueueMonitor = () => {
  const [jobType, setJobType] = useState(undefined);
  const [statuses, setStatuses] = useState(JOB_STATUSES);
  const [activeFilter, setActiveFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [processLimit, setProcessLimit] = useState(25);
  const [actionResult, setActionResult] = useState(null);

  const listVariables = useMemo(
    () =>
      buildListVariables({
        jobType,
        statuses,
        page,
        limit,
        active: activeFilter,
      }),
    [jobType, statuses, page, limit, activeFilter],
  );

  const statsVariables = useMemo(() => (jobType ? { jobType } : {}), [jobType]);

  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery(GET_JOB_QUEUE_STATS, {
    variables: statsVariables,
    fetchPolicy: 'network-only',
    pollInterval: 10000,
  });

  const {
    data: listData,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useQuery(GET_JOB_QUEUE_LIST, {
    variables: listVariables,
    fetchPolicy: 'network-only',
    pollInterval: 10000,
  });

  const [processQueue, { loading: processLoading }] = useMutation(PROCESS_JOB_QUEUE);
  const [retryQueue, { loading: retryLoading }] = useMutation(RETRY_JOB_QUEUE);

  const stats = statsData?.jobQueueStats || {};
  const countsByStatus = stats?.countsByStatus || {};
  const oldestQueued = stats?.oldestQueued || null;
  const failedTerminalCount = stats?.failedTerminalCount || 0;

  const queueListPayload = listData?.jobQueueList || {};
  const jobs = queueListPayload?.jobs || [];
  const total = queueListPayload?.total || 0;

  const refreshAll = async () => {
    await Promise.all([refetchStats(), refetchList()]);
  };

  const handleProcessQueue = async () => {
    setActionResult(null);
    try {
      const variables = {
        maxJobs: processLimit || 25,
      };
      if (jobType) variables.jobType = jobType;

      const response = await processQueue({ variables });
      const result = response?.data?.processJobQueue;
      setActionResult({
        type: 'success',
        message: `Processed ${result?.processed || 0} job(s). Completed: ${
          result?.completed || 0
        }, Retried: ${result?.retried || 0}, Failed: ${result?.failedTerminal || 0}.`,
      });
      await refreshAll();
    } catch (error) {
      message.error('Failed to process queue');
      setActionResult({
        type: 'error',
        message: error?.message || 'Failed to process queue',
      });
    }
  };

  const handleRetryFailed = async () => {
    setActionResult(null);
    try {
      const variables = {
        limit: processLimit || 25,
      };
      if (jobType) variables.jobType = jobType;

      const response = await retryQueue({ variables });
      const result = response?.data?.retryJobQueue || {};
      setActionResult({
        type: 'success',
        message: `Retried ${result?.retried || 0} failed job(s).`,
      });
      await refreshAll();
    } catch (error) {
      message.error('Failed to retry failed jobs');
      setActionResult({
        type: 'error',
        message: error?.message || 'Failed to retry failed jobs',
      });
    }
  };

  const handleRetrySingle = async jobId => {
    try {
      await retryQueue({
        variables: {
          jobId,
        },
      });
      message.success('Failed job queued for retry');
      await refreshAll();
    } catch (error) {
      message.error(error?.message || 'Failed to retry job');
    }
  };

  const columns = [
    {
      title: 'Requested',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 170,
      render: value => formatDate(value),
    },
    {
      title: 'Type',
      dataIndex: 'jobType',
      key: 'jobType',
      width: 140,
      render: value => value || '-',
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 220,
      render: row => (
        <div>
          <div>{row?.entityType || '-'}</div>
          <div className="text-xs text-gray-500">{row?.entityId || '-'}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
    },
    {
      title: 'Attempts',
      key: 'attempts',
      width: 100,
      render: row => `${row?.attempts || 0}/${row?.maxAttempts || 5}`,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: value => (value == null ? '-' : value),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 220,
      render: value => value || '-',
    },
    {
      title: 'Last Error',
      dataIndex: 'lastError',
      key: 'lastError',
      width: 300,
      render: value => {
        if (!value) return '-';
        const text = String(value);
        return text.length > 120 ? `${text.slice(0, 120)}...` : text;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: row => (
        <Button
          size="small"
          disabled={row?.status !== 'FAILED' || retryLoading}
          onClick={() => handleRetrySingle(row?._id)}
        >
          Retry
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {(statsError || listError) && (
        <Alert
          type="error"
          showIcon
          message="Queue data failed to load"
          description={(statsError || listError)?.message}
        />
      )}

      {actionResult && (
        <Alert
          type={actionResult.type === 'error' ? 'error' : 'success'}
          showIcon
          message={actionResult.message}
          closable
          onClose={() => setActionResult(null)}
        />
      )}

      <div className="grid grid-cols-5 gap-2 text-sm">
        <div className="rounded border p-2">
          <div className="text-xs text-gray-500">Queued</div>
          <div className="text-lg font-semibold">{countsByStatus.QUEUED || 0}</div>
        </div>
        <div className="rounded border p-2">
          <div className="text-xs text-gray-500">Running</div>
          <div className="text-lg font-semibold">{countsByStatus.RUNNING || 0}</div>
        </div>
        <div className="rounded border p-2">
          <div className="text-xs text-gray-500">Completed</div>
          <div className="text-lg font-semibold">{countsByStatus.COMPLETED || 0}</div>
        </div>
        <div className="rounded border p-2">
          <div className="text-xs text-gray-500">Failed</div>
          <div className="text-lg font-semibold">{countsByStatus.FAILED || 0}</div>
        </div>
        <div className="rounded border p-2">
          <div className="text-xs text-gray-500">Oldest Queued Age</div>
          <div className="text-lg font-semibold">{formatAge(oldestQueued?.ageMs)}</div>
          <div className="text-xs text-gray-500">Terminal failed: {failedTerminalCount}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          allowClear
          placeholder="Job Type"
          value={jobType}
          style={{ width: 180 }}
          onChange={value => {
            setJobType(value);
            setPage(1);
          }}
        >
          {JOB_TYPES.map(type => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>

        <Select
          mode="multiple"
          value={statuses}
          style={{ minWidth: 260 }}
          placeholder="Statuses"
          onChange={values => {
            setStatuses(values);
            setPage(1);
          }}
        >
          {JOB_STATUSES.map(status => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>

        <Select
          value={activeFilter}
          style={{ width: 140 }}
          onChange={value => {
            setActiveFilter(value);
            setPage(1);
          }}
        >
          <Option value="all">All</Option>
          <Option value="true">Active Only</Option>
          <Option value="false">Inactive Only</Option>
        </Select>

        <InputNumber
          min={1}
          max={200}
          value={processLimit}
          onChange={value => setProcessLimit(value)}
        />

        <Button type="primary" loading={processLoading} onClick={handleProcessQueue}>
          Process Now
        </Button>
        <Button loading={retryLoading} onClick={handleRetryFailed}>
          Retry Failed
        </Button>
        <Button loading={statsLoading || listLoading} onClick={refreshAll}>
          Refresh
        </Button>
      </div>

      <Table
        size="small"
        rowKey={row => row._id}
        loading={statsLoading || listLoading}
        dataSource={jobs}
        columns={columns}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setLimit(nextPageSize || limit);
          },
        }}
        scroll={{ x: 1400 }}
      />
    </div>
  );
};

export default JobQueueMonitor;
